import { FlatQuestion } from "../components/quizFlow.types";
import { QuizAnswers } from "../quizHelpers";

export const QUIZ_CART_RULES = {
  PETS_QUESTION_KEY: "pets-weight",
  PETS_YES_PREFIX: "Yes",
  DOORS_WINDOWS_QUESTION_KEY: "exterior-doors",
  MONITORING_QUESTION_KEY: "additional-monitoring",
  SELF_MONITOR_PREFIX: "I'll monitor",
  SYSTEM_TYPE_QUESTION_KEY: "security-system",
  FULL_SECURITY_PREFIX: "Full security",
  PRODUCT_IDS: {
    DOOR_WINDOW_SENSOR: "door-window-sensor",
    GLASS_BREAK_SENSOR: "glass-break-sensor",
    MOTION_SENSOR: "motion-sensor",
    SELF_PROTECT: "self-protect",
  },
} as const;

export type QuizCartItem = { productId: string; quantity: number };

export type QuizCartPayload = {
  items: QuizCartItem[];
};

export function buildQuizCartPayload(
  answers: QuizAnswers,
  flatQuestions: FlatQuestion[],
): QuizCartPayload {
  const quantities = new Map<string, number>();

  const setQuantity = (productId: string, quantity: number) => {
    if (!productId || quantity <= 0) return;
    quantities.set(productId, quantity);
  };

  const ensureProduct = (productId: string) => {
    if (!productId) return;
    if (!quantities.has(productId)) quantities.set(productId, 1);
  };

  // 1. Collect every option-attached product across answered choice questions.
  //    Backwards compatible: matches by product.id (new), _key (legacy), or
  //    title (display-only options that never carried a product).
  for (const question of flatQuestions) {
    const answer = answers[question.questionKey];
    if (answer === undefined) continue;

    const selectedKeys = normalizeSelectedKeys(answer);
    if (!selectedKeys.length) continue;

    for (const selected of selectedKeys) {
      const option = question.options?.find(
        (o) =>
          o.product?.id === selected ||
          o._key === selected ||
          o.title === selected,
      );
      const productId = option?.product?.id;
      if (productId) ensureProduct(productId);
    }
  }
  // 2. AC6: door / window count drives door-window-sensor quantity.
  const BMS_INCLUDED_DW_SENSORS = 2;
  const doorsWindowsAnswer =
    answers[QUIZ_CART_RULES.DOORS_WINDOWS_QUESTION_KEY];
  const doorsWindowsCount =
    typeof doorsWindowsAnswer === "number" ? doorsWindowsAnswer : 0;
  const extraDwSensors = Math.max(
    0,
    doorsWindowsCount - BMS_INCLUDED_DW_SENSORS,
  );
  if (extraDwSensors > 0) {
    setQuantity(QUIZ_CART_RULES.PRODUCT_IDS.DOOR_WINDOW_SENSOR, extraDwSensors);
  } else {
    quantities.delete(QUIZ_CART_RULES.PRODUCT_IDS.DOOR_WINDOW_SENSOR);
  }
  // 3. AC5: pets > 85 lbs ensures glass-break-sensor is in the cart.
  const petsAnswer = answers[QUIZ_CART_RULES.PETS_QUESTION_KEY];
  const petsYes = matchesPrefix(petsAnswer, QUIZ_CART_RULES.PETS_YES_PREFIX);
  if (petsYes) {
    ensureProduct(QUIZ_CART_RULES.PRODUCT_IDS.GLASS_BREAK_SENSOR);
    quantities.delete(QUIZ_CART_RULES.PRODUCT_IDS.MOTION_SENSOR);
  } else {
    quantities.delete(QUIZ_CART_RULES.PRODUCT_IDS.GLASS_BREAK_SENSOR);
  }

  // 4. Default product rule: if no products were collected from option
  //    selections and the first question is "Full security system", add one
  //    motion sensor. "Just cameras" gets only the kit with no accessories.
  if (quantities.size === 0) {
    const systemTypeAnswer = answers[QUIZ_CART_RULES.SYSTEM_TYPE_QUESTION_KEY];
    if (matchesPrefix(systemTypeAnswer, QUIZ_CART_RULES.FULL_SECURITY_PREFIX)) {
      ensureProduct(QUIZ_CART_RULES.PRODUCT_IDS.MOTION_SENSOR);
    }
  }

  // 5. Materialize and de-dup items.
  const items: QuizCartItem[] = Array.from(quantities.entries())
    .filter(([, quantity]) => quantity > 0)
    .map(([productId, quantity]) => ({ productId, quantity }));

  return { items };
}

function normalizeSelectedKeys(answer: QuizAnswers[string]): string[] {
  if (typeof answer === "string") return [answer];
  if (Array.isArray(answer)) return answer.filter(Boolean);
  return [];
}

function normalizeApostrophes(value: string): string {
  return value.replace(/[\u2018\u2019\u02BC\u02BB]/g, "'");
}

function matchesPrefix(
  answer: QuizAnswers[string] | undefined,
  prefix: string,
): boolean {
  if (typeof answer !== "string") return false;
  const normalized = normalizeApostrophes(answer.trim().toLowerCase());
  const normalizedPrefix = normalizeApostrophes(prefix.toLowerCase());
  return normalized.startsWith(normalizedPrefix);
}

export function getMonitoringOverride(
  answers: QuizAnswers,
): string | undefined {
  const monitoringAnswer = answers[QUIZ_CART_RULES.MONITORING_QUESTION_KEY];
  if (matchesPrefix(monitoringAnswer, QUIZ_CART_RULES.SELF_MONITOR_PREFIX)) {
    return QUIZ_CART_RULES.PRODUCT_IDS.SELF_PROTECT;
  }
  return undefined;
}
